import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Check, Trash2 } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";

import { ApiRequestError, apiRequest } from "../../common/api/apiClient.js";
import { useActiveMembership } from "../../common/membership/ActiveMembership.js";
import type { components } from "../../generated/api.js";

type PlatformTenantDTO = components["schemas"]["PlatformTenantDTO"];

/**
 * Unternehmen plattformweit: anlegen, umbenennen, sperren, löschen.
 *
 * Gesperrt heisst gesperrt — das Team des Mandanten bekommt keinen Kontext mehr und
 * steht vor verschlossener Tür. Gelöscht wird nur, woran keine Buchung hängt: eine
 * Buchung ist ein Beleg, und den räumt man nicht mit dem Mandanten weg.
 */
export function AdminTenantsPage(): JSX.Element {
  const { can } = useActiveMembership();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const tenants = useQuery({
    queryKey: ["platform-tenants"],
    queryFn: () => apiRequest<PlatformTenantDTO[]>("/platform/tenants"),
  });

  const aktualisieren = () => queryClient.invalidateQueries({ queryKey: ["platform-tenants"] });

  const anlegen = useMutation({
    mutationFn: () =>
      apiRequest<PlatformTenantDTO>("/platform/tenants", { method: "POST", body: { name } }),
    onSuccess: async () => {
      setName("");
      await aktualisieren();
    },
  });

  const status = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "ACTIVE" | "SUSPENDED" }) =>
      apiRequest<PlatformTenantDTO>(`/platform/tenants/${id}/status`, {
        method: "POST",
        body: { status },
      }),
    onSuccess: aktualisieren,
  });

  const loeschen = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/platform/tenants/${id}`, { method: "DELETE" }),
    onSuccess: aktualisieren,
  });

  const liste = tenants.data ?? [];
  const fehler = [anlegen.error, status.error, loeschen.error].find(Boolean);

  return (
    <div className="px-5 pb-8">
      <h1 className="pt-5 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Unternehmen</h1>
      <p className="mt-1 max-w-[65ch] text-[13.5px] leading-relaxed text-ink-2">
        Alle Mandanten der Plattform. Ein gesperrtes Unternehmen kann nicht mehr arbeiten —
        sein Team bekommt keinen Zugang mehr, die Daten bleiben.
      </p>

      {can("platform.manage") && (
        <form
          className="mt-4 flex flex-wrap items-end gap-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim().length > 0) {
              anlegen.mutate();
            }
          }}
        >
          <div className="min-w-[14rem] flex-1">
            <label className="micro block" htmlFor="tenant-name">
              Neues Unternehmen
            </label>
            <input
              id="tenant-name"
              className="input mt-1.5"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Kinopolis Rosenheim"
            />
          </div>
          <button type="submit" className="btn" disabled={anlegen.isPending}>
            Anlegen
          </button>
        </form>
      )}

      {fehler && (
        <p className="mt-3 text-[13px] text-danger">
          {fehler instanceof ApiRequestError ? fehler.message : "Die Aktion ist fehlgeschlagen."}
        </p>
      )}

      {tenants.isPending && <p className="mt-5 text-[14px] text-ink-3">Wird geladen …</p>}

      {liste.length > 0 && (
        <div className="card tblscroll mt-5">
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">Unternehmen</th>
                <th scope="col">Status</th>
                <th scope="col" className="text-right">Team</th>
                <th scope="col" className="text-right">Angebote</th>
                <th scope="col" className="text-right">Buchungen</th>
                <th scope="col" className="text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((tenant) => {
                const gesperrt = tenant.status === "SUSPENDED";
                return (
                  <tr key={tenant.id}>
                    <td>
                      <span className="block font-semibold text-ink">{tenant.name}</span>
                      <span className="num text-[11.5px] text-ink-3">{tenant.slug}</span>
                    </td>
                    <td>
                      <span className={gesperrt ? "st st-cancelled" : "st st-confirmed"}>
                        {gesperrt ? "Gesperrt" : "Aktiv"}
                      </span>
                    </td>
                    <td className="num text-right">{tenant.members}</td>
                    <td className="num text-right">{tenant.services}</td>
                    <td className="num text-right">{tenant.bookings}</td>
                    <td className="text-right">
                      <span className="inline-flex gap-1">
                        {can("platform.tenant.block") && (
                          <button
                            type="button"
                            className="grid h-9 w-9 place-items-center rounded-sm text-ink-3 hover:text-ink"
                            onClick={() =>
                              status.mutate({
                                id: tenant.id,
                                status: gesperrt ? "ACTIVE" : "SUSPENDED",
                              })
                            }
                            aria-label={gesperrt ? "Entsperren" : "Sperren"}
                            title={gesperrt ? "Entsperren" : "Sperren"}
                          >
                            {gesperrt ? <Check size={16} /> : <Ban size={16} />}
                          </button>
                        )}
                        {can("platform.manage") && (
                          <button
                            type="button"
                            className="grid h-9 w-9 place-items-center rounded-sm text-ink-3 hover:text-danger disabled:opacity-40"
                            onClick={() => loeschen.mutate(tenant.id)}
                            disabled={tenant.bookings > 0}
                            aria-label="Löschen"
                            title={
                              tenant.bookings > 0
                                ? "Hat Buchungen — sperren statt löschen"
                                : "Löschen"
                            }
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
